import { Dialog, VisuallyHidden } from "radix-ui";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

import styles from "./SideThreadPanel.module.css";
import { speakerName } from "./speakerName";
import { Button } from "../../components/Button";
import {
  AttachmentChips,
  useComposerAttachments,
} from "../../components/composerAttachments";
import { Markdown } from "../../components/Markdown";
import { MessageAttachments } from "../../components/MessageAttachments";
import { ToolCallLine } from "../../components/ToolCallLine";
import { mapAttachment } from "../../data/api/attachments";
import { ApiError } from "../../data/api/client";
import {
  type ApiSideThread,
  type ApiSideThreadMessage,
  createSideThread,
  interruptSideThread,
  patchSideThread,
  postSideThreadMessage,
  useSideThreadMessages,
} from "../../data/api/sideThreads";
import type { Message, MessageRole, ToolCall } from "../../data/types";
import { Icon } from "../../icons";
import { APP_NAME } from "../../lib/appName";
import {
  sideThreadDraftAttachmentsKey,
  sideThreadDraftTextKey,
  usePersistedDraftText,
} from "../../lib/chatDrafts";
import { cx } from "../../lib/cx";
import { markSideThreadRead } from "../../lib/sideThreadReads";
import { relTime } from "../../lib/time";
import { useAutosizeTextarea } from "../../lib/useAutosizeTextarea";

// A side-thread message's `tool_call` into the same view model the primary
// thread's `ToolCallLine` renders — no body, no images. The row below hands it
// no `onToggle` either, which is what keeps it a quiet one-liner
// (side-threads.md §Prototype scope: no expandable bodies, no result images).
const toToolCall = (
  tc: NonNullable<ApiSideThreadMessage["tool_call"]>,
): ToolCall => ({
  arg: tc.argument_summary ?? "",
  body: "",
  bodyLength: null,
  durationMs: tc.duration_ms,
  images: [],
  name: tc.name,
  status: tc.status,
});

// The drawer's width, shared by the panel and the threads list (they are two
// views of the same drawer) and remembered across sessions. Dragging the
// handle on the drawer's right edge resizes it; the clamp keeps it usable on
// small windows.
const WIDTH_KEY = "tin.sideThreadPanelWidth";
const WIDTH_MIN = 320;
const WIDTH_MAX = 760;

const clampWidth = (w: number): number =>
  Math.min(Math.max(w, WIDTH_MIN), Math.min(WIDTH_MAX, window.innerWidth - 80));

export const usePanelWidth = (): {
  readonly onHandlePointerDown: (e: React.PointerEvent) => void;
  readonly width: number;
} => {
  const [width, setWidth] = useState(() => {
    const stored = Number(localStorage.getItem(WIDTH_KEY));
    return Number.isFinite(stored) && stored > 0 ? clampWidth(stored) : 440;
  });

  const onHandlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    // The panel is absolute inside the chat pane, not at the viewport's own
    // left — measure where its edge actually is before turning pointer x
    // into a width.
    const left =
      (e.currentTarget as HTMLElement).parentElement?.getBoundingClientRect()
        .left ?? 0;
    const onMove = (ev: PointerEvent): void => {
      setWidth(clampWidth(ev.clientX - left));
    };
    const onUp = (ev: PointerEvent): void => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      localStorage.setItem(WIDTH_KEY, String(clampWidth(ev.clientX - left)));
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, []);

  return { onHandlePointerDown, width };
};

// The anchor message a side thread is quoted against, in the panel's own
// vocabulary — not `Message` or `ApiChatMessage`, because it has to hold
// together whether it came from a fully-loaded primary-thread row (fork from
// a message) or from a `GET /threads` list row that only carries the anchor's
// body (reopened from the drawer; side-threads.md §UX).
export type SideThreadAnchor = {
  readonly body: string;
  readonly publicId: string;
  readonly role?: MessageRole;
  // The primary thread's `sequence` for this row, when known — what staleness
  // is computed from. Absent when the anchor wasn't in the loaded primary
  // window, in which case the panel simply doesn't claim to know.
  readonly sequence?: number;
  readonly who?: string;
};

export type SideThreadPanelProps = {
  readonly anchor: SideThreadAnchor;
  // The primary-thread messages immediately before the anchor, oldest first
  // — the "historical chat leading up to your question" disclosure. Empty
  // when the anchor isn't in the primary thread's currently-loaded window
  // (reopened from the drawer after paging), in which case the disclosure is
  // simply absent rather than guessing at history the client doesn't hold.
  readonly contextMessages: ReadonlyArray<Message>;
  readonly onBack?: () => void;
  readonly onClose: () => void;
  // The draft's first message landed and the server minted a thread.
  readonly onCreated: (threadId: string) => void;
  readonly onJumpToAnchor: () => void;
  // The primary thread's newest loaded `sequence`, for the staleness note.
  readonly primaryNewestSequence: number;
  readonly taskId: string;
  // Present once the thread exists in the drawer's list; absent for a draft
  // that hasn't sent its first message yet, and for the instant between that
  // send landing and the list's own refetch catching up.
  readonly thread?: ApiSideThread;
  readonly threadId: string | null;
  // The task's workspace, which an attachment upload is scoped to (`api.md`
  // §Chat attachments). Without it the attach affordances are simply absent.
  readonly workspaceId?: string;
};

// Split at the first two sentence-ish breaks, or 160 chars — enough to
// recognise the anchor without turning the panel's header into another
// transcript.
const excerpt = (text: string, max = 200): string =>
  text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;

export const SideThreadPanel = ({
  anchor,
  contextMessages,
  onBack,
  onClose,
  onCreated,
  onJumpToAnchor,
  primaryNewestSequence,
  taskId,
  thread,
  threadId,
  workspaceId,
}: SideThreadPanelProps) => {
  const { messages, refetch } = useSideThreadMessages(taskId, threadId);
  // Keyed on the thread once one exists, and on its anchor before that — the
  // one id a not-yet-created draft thread keeps across a close/reopen of the
  // panel (side-threads.md §UX).
  const draftId = threadId ?? anchor.publicId;
  const [draft, setDraft] = usePersistedDraftText(
    sideThreadDraftTextKey(taskId, draftId),
  );
  const [sending, setSending] = useState(false);
  const [stopping, setStopping] = useState(false);
  // Why the last attempt never left the composer, and which thread's attempt
  // it was — the panel switches threads without remounting (ChatTab's
  // `sendFailure`).
  const [sendFailure, setSendFailure] = useState<{
    readonly draftId: string;
    readonly message: string;
  } | null>(null);
  const [contextOpen, setContextOpen] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { onHandlePointerDown, width } = usePanelWidth();
  useAutosizeTextarea(composerRef, draft);
  // null = not editing; the input holds the draft while it is.
  const [titleDraft, setTitleDraft] = useState<string | null>(null);
  const archived = thread?.archived_at != null;

  const saveTitle = (): void => {
    if (titleDraft === null || !threadId) {
      return;
    }
    const next = titleDraft.trim() || null;
    setTitleDraft(null);
    if (next === (thread?.title ?? null)) {
      return;
    }
    // The list refetches on the realtime `chat.side_thread.updated` this
    // publishes, which is what carries the new title back into `thread`.
    void patchSideThread(taskId, threadId, { title: next });
  };

  const toggleArchive = (): void => {
    if (!threadId) {
      return;
    }
    void patchSideThread(taskId, threadId, { archived: !archived }).then(() => {
      // Archiving is "get this out of my way" — close the panel with it.
      if (!archived) {
        onClose();
      }
    });
  };

  useLayoutEffect(() => {
    composerRef.current?.focus();
  }, []);

  // Reading is having the thread open — stamp on open and again as replies
  // land, so the list's and badge's unread dots clear (lib/sideThreadReads).
  useLayoutEffect(() => {
    if (threadId) {
      markSideThreadRead(threadId);
    }
  }, [threadId, messages]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, sending]);

  // The composer is in the panel's own column, so growing it shrinks the
  // transcript above — keep the newest message in view while typing, unless
  // the reader has scrolled up on purpose.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 160) {
      el.scrollTop = el.scrollHeight;
    }
  }, [draft]);

  const attachEnabled = workspaceId !== undefined;
  const {
    addFiles,
    attachments,
    clear: clearAttachments,
    dragOver,
    readyAttachments,
    removeChip,
    retryChip,
    setDragOver,
    uploadsSettled,
  } = useComposerAttachments(
    workspaceId,
    true,
    sideThreadDraftAttachmentsKey(taskId, draftId),
  );

  const hasContent = draft.trim().length > 0 || readyAttachments.length > 0;
  const canSubmit = !sending && !archived && uploadsSettled && hasContent;

  const send = (): void => {
    if (!canSubmit) {
      return;
    }
    const text = draft.trim();
    const attachmentIds = readyAttachments.map((a) => a.uploaded!.public_id);
    setSending(true);
    setSendFailure(null);
    const request = threadId
      ? postSideThreadMessage(taskId, threadId, text, attachmentIds).then(() =>
          refetch(),
        )
      : createSideThread(taskId, anchor.publicId, text, attachmentIds).then(
          (created) => onCreated(created.thread.public_id),
        );
    void request
      .then(() => {
        clearAttachments();
        setDraft("");
      })
      .catch((err: unknown) => {
        // The draft and the chips stay, so the retry is the same click again.
        setSendFailure({
          draftId,
          message:
            err instanceof ApiError
              ? err.message
              : "Couldn't send that message.",
        });
      })
      .finally(() => setSending(false));
  };

  // Best-effort, like the primary thread's stop: the cue clears on the turn's
  // own realtime update, not on this call resolving.
  const stop = (): void => {
    if (!threadId || stopping) {
      return;
    }
    setStopping(true);
    void interruptSideThread(taskId, threadId).finally(() =>
      setStopping(false),
    );
  };

  // "Main task has moved on" only when we know both ends of the comparison:
  // the anchor's own sequence (absent when it wasn't in the loaded primary
  // window) and the primary thread's current newest (side-threads.md §UX —
  // shown, never inferred past what the client actually holds).
  const staleCount =
    anchor.sequence !== undefined && primaryNewestSequence > anchor.sequence
      ? primaryNewestSequence - anchor.sequence
      : 0;

  // A reply is a whole forked turn away — seconds for a redelivery, minutes
  // for a turn that works before it answers — so say the agent is working
  // rather than sitting silent. Read off the thread's own `turn_started_at`, which
  // the daemon keeps current (side-threads.md §UX): inferring it from the
  // transcript instead lost the cue, and with it the stop button, the moment a
  // turn said anything or ran a tool. `sending` covers the gap before the
  // thread row carrying the new turn has been refetched.
  // Not suppressed on an archived thread: archiving doesn't stop a turn, so
  // hiding the stop button there strands the one thread most in need of it.
  const awaitingReply = sending || thread?.turn_started_at != null;

  return (
    <Dialog.Root
      modal={false}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      open
    >
      {/* No portal, no scrim: the panel is scoped to the chat pane it sits
          in, and the rest of the page — sidebar, header, even the primary
          composer — stays interactive beside it. Clicking anywhere outside
          dismisses it, like Esc; the badge reopens it on this same thread. */}
      <Dialog.Content
        aria-describedby={undefined}
        className={styles.panel}
        style={{ width }}
      >
        <VisuallyHidden.Root>
          <Dialog.Title>{thread?.title ?? "Side thread"}</Dialog.Title>
        </VisuallyHidden.Root>
        <div
          className={styles.resizeHandle}
          onPointerDown={onHandlePointerDown}
        />

        <div className={styles.head}>
          {onBack ? (
            <Button
              icon
              onClick={onBack}
              title="Back to threads"
              variant="ghost"
            >
              <Icon.ArrowLeft size={15} />
            </Button>
          ) : null}
          <span className={styles.headIcon}>
            <Icon.Comment size={15} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            {titleDraft !== null ? (
              <input
                autoFocus
                className={styles.titleInput}
                onBlur={saveTitle}
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    saveTitle();
                  } else if (e.key === "Escape") {
                    // Cancels the edit without closing the dialog around it.
                    e.stopPropagation();
                    setTitleDraft(null);
                  }
                }}
                placeholder="Name this side thread"
                value={titleDraft}
              />
            ) : (
              <button
                className={styles.titleButton}
                disabled={!threadId}
                onClick={() => setTitleDraft(thread?.title ?? "")}
                title="Rename this side thread"
                type="button"
              >
                {thread?.title ?? "Side thread"}
                {archived ? (
                  <span className={styles.archivedTag}>archived</span>
                ) : null}
                {threadId ? (
                  <Icon.Pencil className={styles.titlePencil} size={12} />
                ) : null}
              </button>
            )}
          </div>
          {threadId ? (
            <Button
              icon
              onClick={toggleArchive}
              title={archived ? "Unarchive" : "Archive"}
              variant="ghost"
            >
              <Icon.Archive size={14} />
            </Button>
          ) : null}
          <Button icon onClick={onClose} variant="ghost">
            <Icon.X size={15} />
          </Button>
        </div>

        <div className={styles.anchorBlock}>
          <div className={styles.anchorLabel}>
            <Icon.Branch size={11} />
            Forked from {anchor.who ?? "the primary thread"}
            <a
              className={styles.anchorJump}
              href={`#msg-${anchor.publicId}`}
              onClick={(e) => {
                e.preventDefault();
                onJumpToAnchor();
              }}
            >
              jump to primary thread
            </a>
          </div>
          <div className={styles.anchorBody}>
            <Markdown text={excerpt(anchor.body)} />
          </div>
          {/* Said because the disclosure below sits in the anchor block and
              otherwise reads as the fork's whole context. It isn't: the fork
              resumes the primary session at this point, so it has everything
              up to here (side-threads.md §Context model). The rows are a
              reading aid, capped — hence "the last N", not "the N". */}
          <div className={styles.contextNote}>
            Tinsmith has the chat up to here.
          </div>
          {contextMessages.length > 0 ? (
            <button
              className={styles.contextToggle}
              onClick={() => setContextOpen(!contextOpen)}
              type="button"
            >
              <Icon.ChevDown
                size={11}
                style={{
                  transform: contextOpen ? "rotate(0)" : "rotate(-90deg)",
                  transition: "transform .15s",
                }}
              />
              {contextOpen ? "Hide" : "Show"} the last {contextMessages.length}{" "}
              message{contextMessages.length === 1 ? "" : "s"} before this
            </button>
          ) : null}
          {contextOpen ? (
            <div className={styles.context}>
              {contextMessages.map((m) => (
                <div className={styles.contextRow} key={m.id}>
                  <span className={styles.contextWho}>{speakerName(m)}</span>
                  <span className={styles.contextBody}>
                    {excerpt(m.text ?? "", 120)}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {staleCount > 0 ? (
          <div className={styles.stale}>
            main task has moved on — {staleCount} new message
            {staleCount === 1 ? "" : "s"} since this was forked
          </div>
        ) : null}

        <div className={styles.messages} ref={scrollRef}>
          {threadId === null ? (
            <div className={styles.empty}>
              Send a message below to start this side thread.
            </div>
          ) : (
            messages.map((m) =>
              m.kind === "notice" ? (
                // The server's own words when a turn couldn't run — quiet, not
                // dressed as an agent bubble.
                <div className={styles.notice} key={m.public_id}>
                  {m.body}
                </div>
              ) : m.kind === "tool_use" && m.tool_call ? (
                <ToolCallLine
                  compact
                  key={m.public_id}
                  tool={toToolCall(m.tool_call)}
                />
              ) : (
                <div
                  className={cx(
                    styles.msg,
                    m.role === "agent" && styles.msgAgent,
                  )}
                  key={m.public_id}
                >
                  <div className={styles.msgHead}>
                    <span className={styles.msgWho}>
                      {m.role === "agent" ? APP_NAME : "You"}
                    </span>
                    <span className={styles.msgTs}>
                      {relTime(Date.parse(m.occurred_at))}
                    </span>
                  </div>
                  <div className={styles.msgBody}>
                    <Markdown text={m.body ?? ""} />
                    {m.attachments.length > 0 ? (
                      <MessageAttachments
                        attachments={m.attachments.map(mapAttachment)}
                      />
                    ) : null}
                  </div>
                </div>
              ),
            )
          )}
          {awaitingReply ? (
            <div className={styles.working}>
              <span className={styles.workingDots}>
                <span />
                <span />
                <span />
              </span>
              <span>hammering it out…</span>
              {threadId ? (
                <button
                  className={styles.workingStop}
                  disabled={stopping}
                  onClick={stop}
                  title="Stop this turn"
                  type="button"
                >
                  <Icon.Stop size={11} />
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* States what won't happen rather than what Tinsmith can do: a
            capability claim goes wrong the moment the sandbox is gone, and
            would need liveness plumbed into the panel to say so. The second
            sentence is the surprise the first one never covered — a good
            answer here reaches nobody until you carry it over yourself. */}
        <div className={styles.readOnlyNote}>
          <Icon.Shield size={11} />
          Read-only — Tinsmith can read the code, not change it. Nothing here
          reaches the main thread.
        </div>

        <div
          className={cx(styles.composer, dragOver && styles.composerDrop)}
          onDragLeave={() => setDragOver(false)}
          onDragOver={(e) => {
            if (attachEnabled && e.dataTransfer.types.includes("Files")) {
              e.preventDefault();
              setDragOver(true);
            }
          }}
          onDrop={(e) => {
            if (!attachEnabled) {
              return;
            }
            e.preventDefault();
            setDragOver(false);
            addFiles(e.dataTransfer.files);
          }}
        >
          {sendFailure?.draftId === draftId ? (
            <div className={styles.composerError} role="alert">
              {sendFailure.message}
            </div>
          ) : null}
          <AttachmentChips
            attachments={attachments}
            onRemove={removeChip}
            onRetry={retryChip}
          />
          <div className={styles.composerRow}>
            <textarea
              disabled={sending || archived}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  !e.shiftKey &&
                  !e.nativeEvent.isComposing
                ) {
                  e.preventDefault();
                  send();
                }
              }}
              onPaste={(e) => {
                if (!attachEnabled) {
                  return;
                }
                const files = Array.from(e.clipboardData.files);
                if (files.length > 0) {
                  e.preventDefault();
                  addFiles(files);
                }
              }}
              placeholder={
                archived
                  ? "Archived — unarchive to reply"
                  : "Reply in this side thread… (↵ to send, ⇧↵ for new line)"
              }
              ref={composerRef}
              value={draft}
            />
            <div className={styles.composerButtons}>
              {attachEnabled ? (
                <>
                  <input
                    hidden
                    multiple
                    onChange={(e) => {
                      addFiles(e.target.files ?? []);
                      e.target.value = "";
                    }}
                    ref={fileInputRef}
                    type="file"
                  />
                  <Button
                    icon
                    onClick={() => fileInputRef.current?.click()}
                    title="Attach files"
                    variant="ghost"
                  >
                    <Icon.Paperclip size={14} />
                  </Button>
                </>
              ) : null}
              {/* Says it is sending, for the reason ChatTab's does. */}
              <Button
                disabled={!canSubmit}
                icon
                onClick={send}
                title={sending ? "Sending…" : "Send"}
                variant="accent"
              >
                {sending ? <Icon.Dot3 size={14} /> : <Icon.ArrowUp size={14} />}
              </Button>
            </div>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};
